if (!customElements.get('sticker-preview')) {
  class StickerPreview extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.canvas = this.querySelector('[data-sp-preview-canvas]');
      this.stage = this.querySelector('[data-sp-stage]');
      this.emptyState = this.querySelector('[data-sp-empty-state]');
      this.imageGroup = this.querySelector('[data-sp-image-group]');
      this.imageEl = this.querySelector('[data-sp-preview-image]');
      this.checkerBg = this.querySelector('[data-sp-checker-bg]');
      this.loadingEl = this.querySelector('[data-sp-loading]');
      this.fileFallback = this.querySelector('[data-sp-file-fallback]');
      this.fileName = this.querySelector('[data-sp-file-name]');
      this.ribbon = this.querySelector('[data-sp-ribbon]');
      this.hint = this.querySelector('[data-sp-preview-hint]');
      this.caption = this.querySelector('[data-sp-preview-caption]');
      // Filter primitives: feMorphology (precise expansion) + feFlood (border color)
      this.morphEl = this.querySelector('[data-sp-morph]');
      this.borderFloodEl = this.querySelector('[data-sp-border-flood]');
      // Rectangle clipPath rect, updated to match uploaded image's aspect ratio
      this.clipRectEl = this.querySelector('[data-sp-clip-rect]');

      this.upload = null;
      this.borderColor = null;
      this._loadImg = null;
      this._imgW = 0;
      this._imgH = 0;
      this._imgTransparentRatio = 0;
      this._contentBounds = null;
      this._shapeManuallySelected = false;
      this._isAutoSelecting = false;

      this.SIZE_SCALE = { 2: 0.5, 3: 0.7, 4: 0.85, 5: 1.0 };
    }

    _debug(...args) {
      if (new URLSearchParams(window.location.search).has('spdebug')) {
        console.log('[sticker-preview]', ...args);
      }
    }

    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionChange.bind(this));
      }

      this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', this.handleOptionChange.bind(this));
      }

      document.addEventListener('sticker:upload-change', this.handleUploadChange.bind(this));
      document.addEventListener('sticker:color-change', this.handleColorChange.bind(this));

      // Intercept color input change events in the capture phase on variant-selects so they never
      // reach Shopify's VariantSelects handler, which would otherwise fire renderProductInfo()
      // (XHR + full DOM replacement) and reset the color picker to its Liquid default value.
      this._stopColorChangeRender = (e) => {
        const colorInput = e.target && e.target.closest('[data-sp-color-input]');
        if (!colorInput) return;
        e.stopImmediatePropagation();
        const picker = colorInput.closest('.sp-picker--border');
        const prop = picker && picker.querySelector('[data-sp-color-prop]');
        const hex  = picker && picker.querySelector('[data-sp-color-hex]');
        if (prop) prop.value = colorInput.value;
        if (hex)  hex.textContent = colorInput.value;
        document.dispatchEvent(new CustomEvent('sticker:color-change', { detail: { color: colorInput.value } }));
      };
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', this._stopColorChangeRender, true);
      }

      // Track manual shape selection so auto-detect doesn't override user intent
      this._onShapeChange = (e) => {
        const radio = e.target.closest && e.target.closest(
          `#variant-selects-${this.sectionId} input.sp-tile__radio[data-shape-key]`
        );
        if (radio && !this._isAutoSelecting) {
          this._shapeManuallySelected = true;
          this._debug('shape manually selected:', radio.dataset.shapeKey);
        }
      };
      document.addEventListener('change', this._onShapeChange);

      // Recover upload state that may have fired before this element connected
      if (window.__stickerUpload && !this.upload) {
        this.upload = window.__stickerUpload;
        this._debug('recovered upload from global:', this.upload);
      }

      this._startUploadPoll();
      this._wireEmptyStateClick();

      requestAnimationFrame(() => this.refresh());
    }

    disconnectedCallback() {
      this.unsub && this.unsub();
      if (this._pollTimer) clearInterval(this._pollTimer);
      if (this._onShapeChange) document.removeEventListener('change', this._onShapeChange);
      if (this.variantSelects && this._stopColorChangeRender) {
        this.variantSelects.removeEventListener('change', this._stopColorChangeRender, true);
      }
    }

    _startUploadPoll() {
      this._lastPollVal = '';
      this._pollTimer = setInterval(() => {
        const input = document.getElementById('custom-design-upload');
        if (!input) return;
        const val = input.value || '';
        if (val === this._lastPollVal) return;
        this._lastPollVal = val;
        if (val) {
          const info = (window.__stickerUpload && window.__stickerUpload.cdnUrl === val)
            ? window.__stickerUpload
            : { cdnUrl: val, isImage: true, mimeType: '', filename: '' };
          if (!this.upload || this.upload.cdnUrl !== val) {
            this.upload = info;
            this._shapeManuallySelected = false;
            this._debug('upload detected via poll:', val);
            this.refresh();
          }
        } else if (this.upload) {
          this.upload = null;
          this.refresh();
        }
      }, 400);
    }

    _wireEmptyStateClick() {
      if (!this.emptyState) return;
      this.emptyState.style.cursor = 'pointer';
      this.emptyState.addEventListener('click', () => {
        if (this.upload) return;
        const input = document.getElementById('custom-design-upload');
        if (input && window.uploadcare) {
          try { uploadcare.Widget(input).openDialog(); return; } catch (e) {}
        }
        const btn = document.querySelector('.uploadcare-widget-button, .uploadcare--file');
        if (btn) btn.click();
      });
    }

    handleOptionChange() {
      this.refresh();
    }

    handleUploadChange(event) {
      this.upload = event.detail;
      if (this.upload) {
        this._shapeManuallySelected = false;
        this._imgW = 0;
        this._imgH = 0;
        this._imgTransparentRatio = 0;
        this._contentBounds = null;
      }
      this._debug('upload change event:', event.detail);
      this.refresh();
    }

    handleColorChange(event) {
      this.borderColor = event.detail && event.detail.color;
      if (this.borderColor) {
        const hexSpan = document.querySelector(`#variant-selects-${this.sectionId} [data-sp-color-hex]`);
        if (hexSpan && hexSpan.textContent !== this.borderColor) hexSpan.textContent = this.borderColor;
      }
      this.refresh();
    }

    getSelectedShape() {
      const radio = document.querySelector(`#variant-selects-${this.sectionId} input.sp-tile__radio[data-shape-key]:checked`);
      if (!radio) return null;
      return { key: radio.dataset.shapeKey, noPreview: radio.dataset.noPreview === 'true' };
    }

    getSelectedSize() {
      const radio = document.querySelector(`#variant-selects-${this.sectionId} input.sp-tile__radio[data-size-inches]:checked`);
      if (!radio) return null;
      const inches = parseInt(radio.dataset.sizeInches, 10);
      return { inches, noPreview: radio.dataset.noPreview === 'true' };
    }

    getSelectedBorder() {
      const radio = document.querySelector(`#variant-selects-${this.sectionId} input.sp-tile__radio[data-border-key]:checked`);
      if (!radio) return null;
      return { key: radio.dataset.borderKey, noPreview: radio.dataset.noPreview === 'true' };
    }

    getBorderColor() {
      if (this.borderColor) return this.borderColor;
      const input = document.querySelector(`#variant-selects-${this.sectionId} [data-sp-color-input]`);
      return input ? input.value : '#18181B';
    }

    buildUploadcareUrl(cdnUrl, mimeType) {
      if (!cdnUrl) return null;
      // Client-side processed image (background-removed), use directly, skip CDN transforms
      if (cdnUrl.startsWith('blob:') || cdnUrl.startsWith('data:')) return cdnUrl;
      const base = cdnUrl.endsWith('/') ? cdnUrl : cdnUrl + '/';
      if (mimeType === 'application/pdf') {
        return `${base}-/format/jpeg/-/preview/600x600/-/quality/smart/`;
      }
      return `${base}-/preview/600x600/-/format/auto/-/quality/smart/`;
    }

    refresh() {
      const shape = this.getSelectedShape();
      const size = this.getSelectedSize();
      const border = this.getSelectedBorder();

      const isImageUpload = this.upload && (
        this.upload.isImage ||
        (this.upload.mimeType || '').startsWith('image/') ||
        this.upload.mimeType === 'application/pdf'
      );
      const hasFileUpload = this.upload && !isImageUpload;
      const noPreviewMode = (shape && shape.noPreview) || (size && size.noPreview);

      this.emptyState.style.display = this.upload ? 'none' : '';
      this.imageGroup.style.display = isImageUpload ? '' : 'none';
      this.fileFallback.style.display = hasFileUpload ? '' : 'none';
      this.ribbon.style.display = (this.upload && noPreviewMode) ? '' : 'none';

      if (!this.upload) {
        if (this.loadingEl) this.loadingEl.style.display = 'none';
        this._clearSuggestions();
      }

      if (hasFileUpload && this.fileName) {
        this.fileName.textContent = this.upload.filename || 'design file';
      }

      if (isImageUpload && shape && size) {
        this.applyImage(shape.key, size.inches || 5, this.upload.cdnUrl, noPreviewMode, this.upload.mimeType);
      }

      if (border) {
        this.applyBorder(shape && shape.key, size && size.inches, border.key, noPreviewMode);
      }

      if (shape && size && border) {
        this.hint.textContent = `${size.inches ? size.inches + '"' : 'Custom'} ${shape.key.replace('-', ' ')} · ${border.key} border`;
        this.caption.textContent = noPreviewMode
          ? 'Custom dimensions. Our designer will confirm before printing.'
          : 'Live preview, scaled to actual sticker size.';
      }

      this._debug('refresh', { shape, size, border, isImageUpload, noPreviewMode });
    }

    applyImage(shapeKey, inches, cdnUrl, noPreviewMode, mimeType) {
      const url = this.buildUploadcareUrl(cdnUrl, mimeType);
      if (!url) return;

      const prevUrl = this.imageEl.getAttribute('href');

      if (prevUrl !== url) {
        if (this.loadingEl) this.loadingEl.style.display = '';
        this.imageEl.style.opacity = '0';

        this.imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url);
        this.imageEl.setAttribute('href', url);

        if (this._loadImg) {
          this._loadImg.onload = this._loadImg.onerror = null;
        }
        const img = new Image();
        this._loadImg = img;
        img.onload = () => {
          if (this._loadImg !== img) return;
          this._imgW = img.naturalWidth;
          this._imgH = img.naturalHeight;
          this._debug('image loaded', { w: this._imgW, h: this._imgH });
          if (this.loadingEl) this.loadingEl.style.display = 'none';
          this.imageEl.style.opacity = '1';
          // Re-apply dimensions now that we know the real aspect ratio
          this._applyDimensions(shapeKey, inches, noPreviewMode);
          // Alpha analysis uses a separate CORS-aware PNG request so canvas.getImageData
          // works without tainting the main cached image
          this._runAlphaAnalysis(cdnUrl);
        };
        img.onerror = () => {
          if (this._loadImg !== img) return;
          this._debug('image load error for:', url);
          if (this.loadingEl) this.loadingEl.style.display = 'none';
          this.imageEl.style.opacity = '1';
        };
        img.src = url;
      }

      this._applyDimensions(shapeKey, inches, noPreviewMode);
    }

    _applyDimensions(shapeKey, inches, noPreviewMode) {
      const dims = this.computeShapeDimensions(shapeKey, inches, noPreviewMode);

      this.imageEl.setAttribute('x', dims.x);
      this.imageEl.setAttribute('y', dims.y);
      this.imageEl.setAttribute('width', dims.w);
      this.imageEl.setAttribute('height', dims.h);

      // For rectangle: update the clipPath rect to match the image's actual aspect ratio
      if (shapeKey === 'rectangle' && this.clipRectEl) {
        this.clipRectEl.setAttribute('x', dims.x);
        this.clipRectEl.setAttribute('y', dims.y);
        this.clipRectEl.setAttribute('width', dims.w);
        this.clipRectEl.setAttribute('height', dims.h);
      }

      // Checkerboard always fills the full canvas, no clip.
      // Clipping it to the sticker shape causes a visible seam because the
      // SVG pattern and CSS background checkerboards have different tile origins.
      if (this.checkerBg) {
        this.checkerBg.setAttribute('x', '0');
        this.checkerBg.setAttribute('y', '0');
        this.checkerBg.setAttribute('width', '300');
        this.checkerBg.setAttribute('height', '300');
        this.checkerBg.removeAttribute('clip-path');
      }

      // Die-cut: no clip, the image's own alpha IS the sticker outline.
      // feMorphology reads SourceAlpha to draw the border, tracing the actual
      // cat/design silhouette exactly. The predefined blob clip would restrict
      // the visible area and sever content (ears, edges) past the blob boundary.
      // Geometric shapes: clip-path defines the sticker shape as usual.
      if (noPreviewMode || shapeKey === 'die-cut') {
        this.imageEl.removeAttribute('clip-path');
      } else {
        this.imageEl.setAttribute('clip-path', `url(#sp-clip-${shapeKey}-${this.sectionId})`);
      }

      this._applyContentCrop(shapeKey);
    }

    computeShapeDimensions(shapeKey, inches, noPreviewMode) {
      if (shapeKey === 'circle' || shapeKey === 'die-cut') {
        const r = 140;
        return { x: 150 - r, y: 150 - r, w: r * 2, h: r * 2 };
      }
      if (shapeKey === 'square') {
        const s = 280;
        return { x: 150 - s / 2, y: 150 - s / 2, w: s, h: s };
      }
      if (shapeKey === 'rectangle') {
        const ar = (this._imgW && this._imgH)
          ? Math.max(0.5, Math.min(2.5, this._imgW / this._imgH))
          : (200 / 120);
        let w = Math.round(Math.sqrt(78400 * ar));
        let h = Math.round(w / ar);
        w = Math.max(120, Math.min(280, w));
        h = Math.max(80, Math.min(280, h));
        return { x: Math.round(150 - w / 2), y: Math.round(150 - h / 2), w, h };
      }
      const s = 280;
      return { x: 150 - s / 2, y: 150 - s / 2, w: s, h: s };
    }

    // Border using feMorphology dilate for precise contour-following expansion,
    // then a 0.6px Gaussian blur for sub-pixel anti-aliasing only at the outer edge.
    applyBorder(shapeKey, inches, borderKey, noPreviewMode) {
      if (!this.morphEl || !this.borderFloodEl) return;

      if (borderKey === 'none' || noPreviewMode || !shapeKey) {
        this.borderFloodEl.setAttribute('flood-color', 'transparent');
        return;
      }

      const radius = 4;

      let color = '#ffffff';
      if (borderKey === 'colored') color = this.getBorderColor();
      else if (borderKey === 'unsure') color = '#F97316';

      this.morphEl.setAttribute('radius', radius);
      this.borderFloodEl.setAttribute('flood-color', color);

      this._debug('applyBorder', { borderKey, radius, color });
    }

    // Fetch a small PNG copy of the design (CORS-safe) and read its alpha channel.
    // Separate from the main image load to avoid tainting the cached display image.
    _runAlphaAnalysis(cdnUrl) {
      const mimeType = this.upload ? (this.upload.mimeType || '') : '';
      if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
        this._imgTransparentRatio = 0;
        this._applySuggestions(0, this._imgW / this._imgH);
        return;
      // Background-removed PNG blob, analyse directly without CDN URL transforms
      if (cdnUrl.startsWith('blob:') || cdnUrl.startsWith('data:')) {
        const bImg = new Image();
        bImg.crossOrigin = 'anonymous';
        bImg.onload = () => {
          const ratio = this._analyzeImageAlpha(bImg);
          this._imgTransparentRatio = ratio;
          this._applySuggestions(ratio, this._imgW / this._imgH);
          const sh = this.getSelectedShape(), sz = this.getSelectedSize();
          if (sh && sz) this._applyDimensions(sh.key, sz.inches || 5, (sh.noPreview || sz.noPreview) || false);
        };
        bImg.onerror = () => { this._imgTransparentRatio = 0.5; this._applySuggestions(0.5, this._imgW / this._imgH); };
        bImg.src = cdnUrl;
        return;
      }
      }
      const base = cdnUrl.endsWith('/') ? cdnUrl : cdnUrl + '/';
      const analysisUrl = `${base}-/preview/80x80/-/format/png/`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ratio = this._analyzeImageAlpha(img);
        this._imgTransparentRatio = ratio;
        this._applySuggestions(ratio, this._imgW / this._imgH);
        const shape = this.getSelectedShape();
        const size  = this.getSelectedSize();
        if (shape && size) {
          this._applyDimensions(shape.key, size.inches || 5,
            (shape.noPreview || size.noPreview) || false);
        }
        this._debug('alpha analysis:', { ratio: ratio.toFixed(3), bounds: this._contentBounds });
      };
      img.onerror = () => {
        // CORS or network failed, infer from mimeType
        const likelyTransparent = mimeType.includes('png') || mimeType.includes('svg') || mimeType.includes('webp');
        this._imgTransparentRatio = likelyTransparent ? 0.5 : 0;
        this._applySuggestions(this._imgTransparentRatio, this._imgW / this._imgH);
        this._debug('alpha analysis fallback, mimeType hint:', { likelyTransparent });
      };
      img.src = analysisUrl;
    }

    // Draw image to canvas, count transparent pixels, and compute tight content bounds.
    _analyzeImageAlpha(imgElement) {
      try {
        const SIZE = 80;
        const cv = document.createElement('canvas');
        cv.width = SIZE; cv.height = SIZE;
        const ctx = cv.getContext('2d');
        ctx.drawImage(imgElement, 0, 0, SIZE, SIZE);
        const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
        let transparent = 0;
        let minX = SIZE, maxX = -1, minY = SIZE, maxY = -1;
        for (let py = 0; py < SIZE; py++) {
          for (let px = 0; px < SIZE; px++) {
            const a = data[(py * SIZE + px) * 4 + 3];
            // 50% threshold for die-cut transparent-ratio detection
            if (a < 128) transparent++;
            // 12% threshold for bounds detection, downsampled ear tips can have
            // alpha as low as ~30 at 80px scale, so we use a very conservative cutoff
            if (a >= 32) {
              if (px < minX) minX = px;
              if (px > maxX) maxX = px;
              if (py < minY) minY = py;
              if (py > maxY) maxY = py;
            }
          }
        }
        if (maxX >= minX && maxY >= minY) {
          // Pad by 3 analysis pixels on each side, accounts for quantization error
          // when thin features (1-2px at 80px scale) barely cross the alpha threshold.
          const PAD = 3;
          this._contentBounds = {
            x1: Math.max(0, (minX - PAD) / SIZE),
            y1: Math.max(0, (minY - PAD) / SIZE),
            x2: Math.min(1, (maxX + 1 + PAD) / SIZE),
            y2: Math.min(1, (maxY + 1 + PAD) / SIZE),
          };
        }
        return transparent / (SIZE * SIZE);
      } catch (e) {
        this._debug('canvas read failed:', e.message);
        return 0;
      }
    }

    // Reposition the image element to zoom into the opaque content area, ignoring
    // transparent padding. Geometric clip-paths still apply on top of the larger element.
    // EDGE_MARGIN guarantees content stays that many px from the SVG canvas boundary so
    // the border stroke (radius ≤ EDGE_MARGIN/2) never clips at the container edge.
    _applyContentCrop(shapeKey) {
      if (!this._contentBounds || !this._imgW || !this._imgH) return;
      const { x1, y1, x2, y2 } = this._contentBounds;
      const cw = x2 - x1;
      const ch = y2 - y1;
      if (cw <= 0 || ch <= 0) return;
      if (cw > 0.85 && ch > 0.85) return;

      const EDGE = 20;   // min px from SVG canvas edge, gives border stroke room
      const CANVAS = 300;
      const TARGET = 225; // content fills ~75% of canvas (225 / 300)
      const s = Math.min(TARGET / (cw * this._imgW), TARGET / (ch * this._imgH));
      const imgW = Math.round(this._imgW * s);
      const imgH = Math.round(this._imgH * s);
      // Center content at canvas center then clamp so content edges stay ≥ EDGE from boundary
      let imgX = Math.round(150 - ((x1 + x2) / 2) * imgW);
      let imgY = Math.round(150 - ((y1 + y2) / 2) * imgH);
      const cL = imgX + x1 * imgW, cT = imgY + y1 * imgH;
      const cR = imgX + x2 * imgW, cB = imgY + y2 * imgH;
      if (cL < EDGE)           imgX += Math.round(EDGE - cL);
      if (cT < EDGE)           imgY += Math.round(EDGE - cT);
      if (cR > CANVAS - EDGE)  imgX -= Math.round(cR - (CANVAS - EDGE));
      if (cB > CANVAS - EDGE)  imgY -= Math.round(cB - (CANVAS - EDGE));

      this.imageEl.setAttribute('x', imgX);
      this.imageEl.setAttribute('y', imgY);
      this.imageEl.setAttribute('width', imgW);
      this.imageEl.setAttribute('height', imgH);
      this._debug('contentCrop', { imgX, imgY, imgW, imgH, cw: cw.toFixed(3), ch: ch.toFixed(3) });
    }

    // Programmatically select a shape, only if user hasn't manually chosen one.
    _autoSelectShape(shapeKey) {
      if (this._shapeManuallySelected) return;
      const radio = document.querySelector(
        `#variant-selects-${this.sectionId} input.sp-tile__radio[data-shape-key="${shapeKey}"]`
      );
      if (radio && !radio.checked) {
        this._isAutoSelecting = true;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        this._isAutoSelecting = false;
        this._debug('auto-selected shape:', shapeKey);
      }
    }

    // Apply "Recommended" and "Crops your design" badges to shape tiles based on image analysis.
    _applySuggestions(transparentRatio, aspectRatio) {
      const sectionRoot = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!sectionRoot) return;

      const isDieCutCandidate = transparentRatio > 0.15;

      if (isDieCutCandidate) {
        this._autoSelectShape('die-cut');
      }

      sectionRoot.querySelectorAll('[data-sp-shape-suggest]').forEach(suggest => {
        const label = suggest.closest('[data-shape-key]');
        if (!label) return;
        const shapeKey = label.dataset.shapeKey;

        suggest.textContent = '';
        delete suggest.dataset.suggestType;

        if (isDieCutCandidate) {
          if (shapeKey === 'die-cut') {
            suggest.textContent = 'Recommended';
            suggest.dataset.suggestType = 'recommend';
          } else if (['circle', 'square', 'rectangle'].includes(shapeKey)) {
            suggest.textContent = 'Crops your design';
            suggest.dataset.suggestType = 'warn';
          }
        } else {
          // Opaque image: suggest best shape by aspect ratio
          const suggestedShapes = aspectRatio >= 1.3 ? ['rectangle'] : ['square', 'circle'];
          if (suggestedShapes.includes(shapeKey)) {
            suggest.textContent = 'Recommended';
            suggest.dataset.suggestType = 'recommend';
          }
        }
      });
    }

    // Clear all suggestion badges (called when upload is removed).
    _clearSuggestions() {
      const sectionRoot = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!sectionRoot) return;
      sectionRoot.querySelectorAll('[data-sp-shape-suggest]').forEach(suggest => {
        suggest.textContent = '';
        delete suggest.dataset.suggestType;
      });
    }
  }

  customElements.define('sticker-preview', StickerPreview);
}

// Selected-state syncing for picker tiles + color picker wiring.
(function () {
  document.addEventListener('change', function (e) {
    const radio = e.target.closest && e.target.closest('input.sp-tile__radio');
    if (radio) {
      const fs = radio.closest('fieldset');
      if (fs) {
        fs.querySelectorAll('.sp-tile').forEach((t) => t.classList.remove('sp-tile--selected'));
        const lbl = fs.querySelector(`label[for="${radio.id}"]`);
        if (lbl) lbl.classList.add('sp-tile--selected');
        const selectedSpan = fs.querySelector('[data-selected-value]');
        if (selectedSpan) selectedSpan.textContent = radio.value;
      }

      if (radio.dataset.borderKey) {
        const picker = radio.closest('.sp-picker--border');
        if (picker) {
          const colorPicker = picker.querySelector('[data-sp-color-picker]');
          if (colorPicker) {
            const show = radio.dataset.borderKey === 'colored';
            colorPicker.hidden = !show;
            const prop = picker.querySelector('[data-sp-color-prop]');
            const input = picker.querySelector('[data-sp-color-input]');
            if (prop && input) {
              prop.value = show ? input.value : '';
            }
          }
        }
      }
    }

    const colorInput = e.target.closest && e.target.closest('[data-sp-color-input]');
    if (colorInput) {
      const picker = colorInput.closest('.sp-picker--border');
      const prop = picker && picker.querySelector('[data-sp-color-prop]');
      const hex = picker && picker.querySelector('[data-sp-color-hex]');
      if (prop) prop.value = colorInput.value;
      if (hex) hex.textContent = colorInput.value;
      document.dispatchEvent(new CustomEvent('sticker:color-change', { detail: { color: colorInput.value } }));
    }
  });

  document.addEventListener('input', function (e) {
    const colorInput = e.target.closest && e.target.closest('[data-sp-color-input]');
    if (colorInput) {
      const picker = colorInput.closest('.sp-picker--border');
      const hex = picker && picker.querySelector('[data-sp-color-hex]');
      if (hex) hex.textContent = colorInput.value;
      document.dispatchEvent(new CustomEvent('sticker:color-change', { detail: { color: colorInput.value } }));
    }
  });

  function syncInitialSelections() {
    document.querySelectorAll('input.sp-tile__radio:checked').forEach((radio) => {
      const lbl = document.querySelector(`label[for="${radio.id}"]`);
      if (lbl) lbl.classList.add('sp-tile--selected');
      if (radio.dataset.borderKey === 'colored') {
        const picker = radio.closest('.sp-picker--border');
        if (picker) {
          const colorPicker = picker.querySelector('[data-sp-color-picker]');
          if (colorPicker) colorPicker.hidden = false;
          const prop = picker.querySelector('[data-sp-color-prop]');
          const input = picker.querySelector('[data-sp-color-input]');
          if (prop && input) prop.value = input.value;
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncInitialSelections);
  } else {
    syncInitialSelections();
  }
})();
